import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Badge,
  CircularProgress,
  InputAdornment,
  Menu,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Send as SendIcon,
  Chat as ChatIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import Select from './SafeSelect';
import { useAdmin } from '../context/AdminContext';
import adminChatService from '../services/adminChatService';

const ChatSupport = () => {
  const { user, drivers } = useAdmin();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkTitle, setBulkTitle] = useState('');
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkTargetMode, setBulkTargetMode] = useState('all');
  const [bulkSelectedDriverIds, setBulkSelectedDriverIds] = useState([]);
  const messagesEndRef = useRef(null);
  const adminDisplayName = user?.name || user?.displayName || user?.email || 'Admin';

  const toTimestampMs = (timestamp) => {
    if (!timestamp) return 0;
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  };

  const chatItems = useMemo(() => {
    const conversationsByDriverId = new Map();

    conversations.forEach((conversation) => {
      if (conversation.driverId) {
        conversationsByDriverId.set(conversation.driverId, conversation);
      }
    });

    const driverItems = (drivers || []).map((driver) => {
      const driverId = driver.userId || driver.id;
      const matchedConversation = driverId
        ? conversationsByDriverId.get(driverId)
        : null;

      if (driverId) {
        conversationsByDriverId.delete(driverId);
      }

      return {
        id: matchedConversation?.id || null,
        driverId: driverId || driver.id,
        driverName:
          driver.name ||
          driver.fullName ||
          driver.driverName ||
          driver.email ||
          'Driver',
        lastMessage: matchedConversation?.lastMessage || null,
        lastMessageTime: matchedConversation?.lastMessageTime || null,
        unreadByAdmin: matchedConversation?.unreadByAdmin || 0,
      };
    });

    const orphanConversationItems = Array.from(conversationsByDriverId.values()).map((conversation) => ({
      id: conversation.id,
      driverId: conversation.driverId || conversation.id,
      driverName: conversation.driverName || 'Driver',
      lastMessage: conversation.lastMessage || null,
      lastMessageTime: conversation.lastMessageTime || null,
      unreadByAdmin: conversation.unreadByAdmin || 0,
    }));

    const mergedItems = [...driverItems, ...orphanConversationItems];

    mergedItems.sort((firstItem, secondItem) => {
      const firstMessageTimestamp = toTimestampMs(firstItem.lastMessageTime);
      const secondMessageTimestamp = toTimestampMs(secondItem.lastMessageTime);

      if (firstMessageTimestamp !== secondMessageTimestamp) {
        return secondMessageTimestamp - firstMessageTimestamp;
      }

      return (firstItem.driverName || '').localeCompare(secondItem.driverName || '');
    });

    return mergedItems;
  }, [conversations, drivers]);

  const bulkTargetDrivers = useMemo(() => {
    const uniqueTargets = new Map();

    (drivers || []).forEach((driver) => {
      const driverId = driver?.userId || driver?.uid || driver?.id || '';
      if (!driverId || uniqueTargets.has(driverId)) {
        return;
      }

      uniqueTargets.set(driverId, {
        driverId,
        driverName:
          driver?.name ||
          driver?.fullName ||
          driver?.driverName ||
          driver?.email ||
          'Driver',
      });
    });

    return Array.from(uniqueTargets.values());
  }, [drivers]);

  const bulkSelectedDrivers = useMemo(() => {
    const selectedIds = new Set(
      (bulkSelectedDriverIds || []).map((driverId) => String(driverId))
    );

    return bulkTargetDrivers.filter((targetDriver) =>
      selectedIds.has(String(targetDriver.driverId))
    );
  }, [bulkSelectedDriverIds, bulkTargetDrivers]);

  // Decrypt last message for preview
  const decryptPreview = (encryptedText) => {
    try {
      return adminChatService.decryptMessage(encryptedText);
    } catch {
      return 'Message...';
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedConversation?.id) return;

    try {
      await adminChatService.deleteConversation(selectedConversation.id);
      setDeleteDialogOpen(false);
      setSelectedConversation(null);
      setMessages([]);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Failed to delete conversation');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleOpenBulkDialog = () => {
    setBulkTargetMode('all');
    setBulkSelectedDriverIds([]);
    setBulkDialogOpen(true);
  };

  const handleCloseBulkDialog = () => {
    if (bulkSending) {
      return;
    }
    setBulkDialogOpen(false);
  };

  const handleBulkSend = async () => {
    const messageToSend = String(bulkMessage || '').trim();

    if (!messageToSend) {
      alert('Please enter a notification message to send.');
      return;
    }

    if (!user?.uid) {
      alert('You must be signed in as admin to send bulk notifications.');
      return;
    }

    if (bulkTargetDrivers.length === 0) {
      alert('No drivers are available to receive this bulk notification.');
      return;
    }

    const targetDrivers =
      bulkTargetMode === 'selected' ? bulkSelectedDrivers : bulkTargetDrivers;

    if (targetDrivers.length === 0) {
      alert('Please select at least one driver for this bulk message.');
      return;
    }

    try {
      setBulkSending(true);

      const result = await adminChatService.sendBulkMessageToDrivers(
        targetDrivers.map((targetDriver) => ({
          userId: targetDriver.driverId,
          name: targetDriver.driverName,
        })),
        messageToSend,
        user.uid,
        adminDisplayName,
        bulkTitle
      );

      const baseMessage = `Bulk notification sent to ${result.successCount}/${result.totalTargets} drivers.`;
      if (result.failedCount > 0) {
        alert(`${baseMessage}\n${result.failedCount} delivery failure(s).`);
      } else {
        alert(baseMessage);
      }

      setBulkTitle('');
      setBulkMessage('');
      setBulkTargetMode('all');
      setBulkSelectedDriverIds([]);
      setBulkDialogOpen(false);
    } catch (error) {
      console.error('Error sending bulk notification:', error);
      alert(error?.message || 'Failed to send bulk notification.');
    } finally {
      setBulkSending(false);
    }
  };

  useEffect(() => {
    // Subscribe to all conversations
    const unsubscribe = adminChatService.subscribeToConversations((convs) => {
      setConversations(convs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!selectedConversation?.id) {
      setMessages([]);
      return;
    }

    // Subscribe to messages in selected conversation
    const unsubscribe = adminChatService.subscribeToMessages(
      selectedConversation.id,
      (msgs) => {
        setMessages(msgs);
        scrollToBottom();
      }
    );

    // Mark messages as read
    adminChatService.markMessagesAsRead(selectedConversation.id);

    return () => unsubscribe();
  }, [selectedConversation?.id]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation || sending) return;

    try {
      setSending(true);
      const textToSend = messageText.trim();
      setMessageText('');

      let conversationId = selectedConversation.id;
      if (!conversationId) {
        conversationId = await adminChatService.getOrCreateConversation(
          selectedConversation.driverId,
          selectedConversation.driverName
        );

        setSelectedConversation((currentSelection) =>
          currentSelection
            ? { ...currentSelection, id: conversationId }
            : currentSelection
        );
      }

      await adminChatService.sendMessage(
        conversationId,
        textToSend,
        user.uid,
        adminDisplayName
      );
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    // Less than 24 hours
    if (diff < 86400000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // Less than 7 days
    if (diff < 604800000) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    }
    
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getMessageSenderName = (msg) => {
    if (msg?.senderName) {
      return msg.senderName;
    }

    if (msg?.senderType === 'admin') {
      return 'Admin';
    }

    return selectedConversation?.driverName || 'Driver';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 150px)', gap: 2 }}>
      {/* Conversations List */}
      <Paper sx={{ width: 320, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Driver Chats</Typography>
          <Typography variant="body2" color="text.secondary">
            {chatItems.length} driver{chatItems.length !== 1 ? 's' : ''}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CampaignIcon />}
            onClick={handleOpenBulkDialog}
            disabled={bulkSending || bulkTargetDrivers.length === 0}
            sx={{ mt: 1.5 }}
          >
            Bulk Notification
          </Button>
        </Box>
        
        <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
          {chatItems.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <ChatIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No drivers available</Typography>
            </Box>
          ) : (
            chatItems.map((chatItem) => (
              <React.Fragment key={`${chatItem.driverId}-${chatItem.id || 'new'}`}>
                <ListItemButton
                  selected={selectedConversation?.driverId === chatItem.driverId}
                  onClick={() => setSelectedConversation(chatItem)}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" noWrap>
                          {chatItem.driverName}
                        </Typography>
                        {chatItem.unreadByAdmin > 0 && (
                          <Badge badgeContent={chatItem.unreadByAdmin} color="error" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" noWrap color="text.secondary">
                          {chatItem.lastMessage ? decryptPreview(chatItem.lastMessage) : 'Start a conversation'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(chatItem.lastMessageTime)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
                <Divider />
              </React.Fragment>
            ))
          )}
        </List>
      </Paper>

      {/* Messages Area */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">{selectedConversation.driverName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Driver Support Chat
                </Typography>
              </Box>
              {selectedConversation.id && (
                <>
                  <IconButton onClick={handleMenuOpen}>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={handleDeleteClick}>
                      <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                      Delete Conversation
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {messages.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No messages yet</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start the conversation
                  </Typography>
                </Box>
              ) : (
                messages.map((msg) => {
                  const isAdmin = msg.senderType === 'admin';
                  const senderName = getMessageSenderName(msg);
                  
                  return (
                    <Box
                      key={msg.id}
                      sx={{
                        display: 'flex',
                        justifyContent: isAdmin ? 'flex-end' : 'flex-start',
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '70%',
                          bgcolor: isAdmin ? '#C8102E' : '#f5f5f5',
                          color: isAdmin ? '#fff' : '#000',
                          borderRadius: 2,
                          p: 1.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mb: 0.5,
                            fontWeight: 600,
                            color: isAdmin ? 'rgba(255,255,255,0.85)' : 'text.secondary',
                          }}
                        >
                          {senderName}
                        </Typography>
                        <Typography variant="body1">{msg.text}</Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            opacity: 0.7,
                          }}
                        >
                          {formatTime(msg.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Input Area */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Type a message..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={sending}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!messageText.trim() || sending}
                      >
                        {sending ? <CircularProgress size={24} /> : <SendIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            <ChatIcon sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6">Select a conversation</Typography>
            <Typography variant="body2">
              Choose a chat from the list to start messaging
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this entire conversation with {selectedConversation?.driverName}?
            This will permanently delete all messages and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Notification Dialog */}
      <Dialog open={bulkDialogOpen} onClose={handleCloseBulkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Send Bulk Notification</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Send a broadcast message in chat to all drivers or selected drivers.
          </DialogContentText>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Recipients</InputLabel>
            <Select
              label="Recipients"
              value={bulkTargetMode}
              onChange={(event) => {
                setBulkTargetMode(event.target.value);
                if (event.target.value !== 'selected') {
                  setBulkSelectedDriverIds([]);
                }
              }}
              disabled={bulkSending}
            >
              <MenuItem value="all">
                All Drivers ({bulkTargetDrivers.length})
              </MenuItem>
              <MenuItem value="selected">Selected Drivers</MenuItem>
            </Select>
          </FormControl>

          {bulkTargetMode === 'selected' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Drivers</InputLabel>
              <Select
                multiple
                value={bulkSelectedDriverIds}
                onChange={(event) => {
                  const value = event.target.value;
                  setBulkSelectedDriverIds(
                    typeof value === 'string' ? value.split(',') : value
                  );
                }}
                input={<OutlinedInput label="Select Drivers" />}
                renderValue={(selectedIds) => {
                  const names = bulkTargetDrivers
                    .filter((targetDriver) =>
                      selectedIds.includes(targetDriver.driverId)
                    )
                    .map((targetDriver) => targetDriver.driverName);

                  return names.join(', ');
                }}
                disabled={bulkSending}
              >
                {bulkTargetDrivers.map((targetDriver) => (
                  <MenuItem
                    key={targetDriver.driverId}
                    value={targetDriver.driverId}
                  >
                    <Checkbox
                      checked={bulkSelectedDriverIds.includes(
                        targetDriver.driverId
                      )}
                    />
                    <ListItemText
                      primary={targetDriver.driverName}
                      secondary={targetDriver.driverId}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label="Title (optional)"
            value={bulkTitle}
            onChange={(event) => setBulkTitle(event.target.value)}
            sx={{ mb: 2 }}
            disabled={bulkSending}
            placeholder="Service update"
          />
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Message"
            value={bulkMessage}
            onChange={(event) => setBulkMessage(event.target.value)}
            disabled={bulkSending}
            placeholder="Type the custom notification message for all drivers..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkDialog} disabled={bulkSending}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkSend}
            variant="contained"
            startIcon={<CampaignIcon />}
            disabled={
              bulkSending ||
              !bulkMessage.trim() ||
              (bulkTargetMode === 'selected' && bulkSelectedDriverIds.length === 0)
            }
          >
            {bulkSending
              ? 'Sending...'
              : bulkTargetMode === 'selected'
                ? `Send to Selected (${bulkSelectedDriverIds.length})`
                : 'Send to All Drivers'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatSupport;
